import { AppConfig, FEATURE_FLAGS, FeatureFlag, VIDEO_FORMATS, VideoFormat } from './types';

export class ConfigValidationError extends Error {
  readonly issues: string[];
  constructor(issues: string[]) {
    super('student-config is invalid:\n' + issues.map(i => ` - ${i}`).join('\n'));
    this.name = 'ConfigValidationError';
    this.issues = issues;
  }
}

// Byte-identical to the backend resolver. NO 'i' flag: case-sensitive, ASCII.
const GA_MEASUREMENT_ID_RE = /^(G|UA|GT|AW|DC)-[A-Z0-9-]+$/;
const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

function isAbsoluteHttpUrl(v: unknown): boolean {
  if (typeof v !== 'string' || v.length === 0) return false;
  try {
    const u = new URL(v);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}

export function validateConfig(raw: unknown): AppConfig {
  if (typeof raw !== 'object' || raw === null) {
    throw new ConfigValidationError(['config root is not an object']);
  }
  const c = raw as any;
  const issues: string[] = [];

  if (c.version !== 1) issues.push('version must be exactly 1');

  const tenant = c.tenant ?? {};
  if (!(Number.isInteger(tenant.id) && tenant.id > 0)) {
    issues.push('tenant.id must be a positive integer');
  }
  if (!isNonEmptyString(tenant.siteName)) issues.push('tenant.siteName must be a non-empty string');
  if (!isNonEmptyString(tenant.siteTitle)) issues.push('tenant.siteTitle must be a non-empty string');

  const features = c.features ?? {};
  for (const flag of FEATURE_FLAGS) {
    if (typeof features[flag] !== 'boolean') issues.push(`features.${flag} must be a boolean`);
  }

  const videoFormats = features.videoFormats;
  const isFormat = (f: unknown): f is VideoFormat =>
    (VIDEO_FORMATS as readonly string[]).includes(f as string);
  if (!Array.isArray(videoFormats) || videoFormats.length === 0 || !videoFormats.every(isFormat)) {
    issues.push(`features.videoFormats must be a non-empty array of ${VIDEO_FORMATS.join('/')}`);
  }

  const branding = c.branding ?? {};
  if (!isAbsoluteHttpUrl(branding.logoUrl)) issues.push('branding.logoUrl must be an absolute http(s) URL');
  if (!isAbsoluteHttpUrl(branding.faviconUrl)) issues.push('branding.faviconUrl must be an absolute http(s) URL');
  if (typeof branding.brandColor !== 'string' || !HEX_COLOR_RE.test(branding.brandColor)) {
    issues.push('branding.brandColor must be a hex color like #7B2C29');
  }

  const copy = c.copy ?? {};
  if (!isNonEmptyString(copy.idLabel)) issues.push('copy.idLabel must be a non-empty string');
  if (!isNonEmptyString(copy.idPlaceholder)) issues.push('copy.idPlaceholder must be a non-empty string');
  if (!isNonEmptyString(copy.loginInstruction)) issues.push('copy.loginInstruction must be a non-empty string');

  const urls = c.urls ?? {};
  if (!isAbsoluteHttpUrl(urls.chatbotApiBase)) issues.push('urls.chatbotApiBase must be an absolute http(s) URL');

  // Consent copy + release form are optional for every tenant, payments or not
  // (matches the backend): absent means the consent block is not rendered at
  // all. Present-but-malformed is still an error.
  if (!(copy.shareConsentText == null || isNonEmptyString(copy.shareConsentText))) {
    issues.push('copy.shareConsentText must be null or a non-empty string');
  }
  if (!(urls.releaseFormUrl == null || isAbsoluteHttpUrl(urls.releaseFormUrl))) {
    issues.push('urls.releaseFormUrl must be null or an absolute http(s) URL');
  }

  // The whole upscaleComparison section is optional: tenants seeded before
  // gradcut-backend#124 have no such key, and the modal falls back to the
  // images bundled with the app. Absent section => both URLs null. Each URL
  // individually may be null/absent; present-but-malformed is still an error.
  const upscaleComparison = c.upscaleComparison ?? {};
  if (!(upscaleComparison.beforeUrl == null || isAbsoluteHttpUrl(upscaleComparison.beforeUrl))) {
    issues.push('upscaleComparison.beforeUrl must be null or an absolute http(s) URL');
  }
  if (!(upscaleComparison.afterUrl == null || isAbsoluteHttpUrl(upscaleComparison.afterUrl))) {
    issues.push('upscaleComparison.afterUrl must be null or an absolute http(s) URL');
  }

  const analytics = c.analytics ?? {};
  const ga = analytics.gaPropertyId;
  if (!(ga === null || (typeof ga === 'string' && GA_MEASUREMENT_ID_RE.test(ga)))) {
    issues.push('analytics.gaPropertyId must be null or a gtag Measurement ID (e.g. G-XXXX)');
  }

  if (issues.length > 0) throw new ConfigValidationError(issues);

  return {
    version: 1,
    tenant: { id: tenant.id, siteName: tenant.siteName, siteTitle: tenant.siteTitle },
    features: {
      ...FEATURE_FLAGS.reduce((acc, f) => {
        acc[f] = features[f];
        return acc;
      }, {} as Record<FeatureFlag, boolean>),
      videoFormats: [...videoFormats],
    },
    branding: { logoUrl: branding.logoUrl, faviconUrl: branding.faviconUrl, brandColor: branding.brandColor },
    copy: {
      idLabel: copy.idLabel,
      idPlaceholder: copy.idPlaceholder,
      loginInstruction: copy.loginInstruction,
      shareConsentText: copy.shareConsentText ?? null,
    },
    urls: { chatbotApiBase: urls.chatbotApiBase, releaseFormUrl: urls.releaseFormUrl ?? null },
    analytics: { gaPropertyId: ga },
    upscaleComparison: {
      beforeUrl: upscaleComparison.beforeUrl ?? null,
      afterUrl: upscaleComparison.afterUrl ?? null,
    },
  };
}
